import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import Navbar from "../components/Navbar";
import useGroupBasket from "../hooks/useGroupBasket";
import useGroupPaymentWatch from "../hooks/useGroupPaymentWatch";
import { forgetMyGroupBaskets } from "../hooks/useMyGroupBaskets";
import useRestaurantMenuSections from "../hooks/useRestaurantMenuSections";
import { customerIdOf } from "../hooks/useAnalyticsUser";
import {
  cancelGroupBasket,
  lockGroupBasket,
  removeGroupMember,
  saveMyGroupLines,
} from "../api/groupBasketApi";
import { verifyAirtelPayment } from "../redux/airtelSseRedux";
import { growthEvent } from "../utils/analytics";
import {
  CONTROL,
  MEMBER_CONTROL,
  REFUND_CONFIRMATION_REQUIRED,
  REFUND_CONFIRMATION_TEXT,
  basketControls,
  cancelNeedsRefundConfirmation,
  contributionOutcome,
  isHostOf,
  leadOf,
  linesWithSaved,
  linesWithout,
  memberControls,
  myMemberOf,
  modeSummary,
  stateLabel,
  transitionEvents,
} from "../utils/groupBasketView";
import GroupBasketCountdown from "../components/GroupBasket/GroupBasketCountdown";
import GroupBasketShare from "../components/GroupBasket/GroupBasketShare";
import GroupBasketMoney from "../components/GroupBasket/GroupBasketMoney";
import GroupBasketMembers from "../components/GroupBasket/GroupBasketMembers";
import GroupBasketOrder from "../components/GroupBasket/GroupBasketOrder";
import GroupMenuSheet from "../components/GroupBasket/GroupMenuSheet";
import GroupDishDialog from "../components/GroupBasket/GroupDishDialog";
import GroupPaySheet, { PAY_PURPOSE } from "../components/GroupBasket/GroupPaySheet";
import GroupConfirmDialog from "../components/GroupBasket/GroupConfirmDialog";
import {
  Actions,
  Card,
  CardTitle,
  Eyebrow,
  FieldLabel,
  Hero,
  HeroInner,
  HeroLine,
  HeroTitle,
  InkButton,
  LINE,
  MUTED,
  Muted,
  Notice,
  Page,
  Pill,
  PrimaryButton,
  QuietButton,
  Shell,
  TEXT,
  TextButton,
  TextInput,
} from "../components/GroupBasket/groupBasketStyles";

const CONTROL_LABELS = {
  [CONTROL.ADD_ITEMS]: "Add my items",
  [CONTROL.LOCK]: "Lock the basket now",
  [CONTROL.CHECKOUT]: "Check out for everyone",
  [CONTROL.PAY_SHARE]: "Pay my share",
  [CONTROL.PAY_REMAINDER]: "Pay what is left",
  [CONTROL.OPEN_ORDER]: "Open the order",
};

const PAY_PURPOSE_OF = {
  [CONTROL.CHECKOUT]: PAY_PURPOSE.CHECKOUT,
  [CONTROL.PAY_SHARE]: PAY_PURPOSE.SHARE,
  [CONTROL.PAY_REMAINDER]: PAY_PURPOSE.REMAINDER,
};

/* The basket's restaurant, as its storefront opens it. */
const restaurantPathOf = (vendor) => {
  if (!vendor?.vendorId) return "/restaurants";
  const outlet = vendor.outletId ? `?outletId=${encodeURIComponent(vendor.outletId)}` : "";
  return `/restaurant/shop/${encodeURIComponent(vendor.vendorId)}${outlet}`;
};

/* Query parameters a payment provider adds on the way back. */
const RETURN_PARAMS = ["resp", "status", "tx_ref", "transaction_id", "response", "created"];

/*
 * One Order together basket, for its members (/group-basket/:basketId). It
 * renders the server's basket and nothing else: `nextAction` leads, each
 * control exists only while its permission is true, every amount is the
 * server's. Live through the customer's socket room, polled every 10 seconds
 * while that is down.
 */
const GroupBasketPage = () => {
  const { basketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.currentUser);
  const userId = customerIdOf(user);

  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dish, setDish] = useState(null); // { product, line }
  const [payPurpose, setPayPurpose] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, member }
  const [confirmError, setConfirmError] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNeedsConfirmation, setCancelNeedsConfirmation] = useState(false);

  /* "?created=1" (just opened) leads with the share card. Read once. */
  const [justCreated] = useState(() => new URLSearchParams(location.search).get("created") === "1");

  const paymentRef = useRef(null);
  const forgetRef = useRef(() => {});

  // Every newer basket: the growth events it proves, and the payment this
  // browser started settling or being declined.
  const onChange = useCallback((previous, next) => {
    const payment = paymentRef.current;
    const shareRail = payment?.role === "share" ? payment.analyticsRail : null;
    transitionEvents(previous, next, { shareRail }).forEach(({ name, params }) =>
      growthEvent(name, params)
    );
    // An ended basket leaves the resume cards.
    if (previous && previous.state !== next.state && ["RELEASED", "CANCELLED"].includes(next.state)) {
      forgetMyGroupBaskets();
    }
    if (!payment || !previous) return;
    const result = contributionOutcome(payment.role, previous, next);
    if (!result) return;
    if (result.outcome === "paid") toast.success("Payment confirmed.");
    if (result.outcome === "declined") {
      toast.error(
        result.reason
          ? `The payment did not go through: ${result.reason}. You can try again.`
          : "The payment did not go through. You can try again."
      );
    }
    forgetRef.current();
  }, []);

  const { basket, status, error, receivedAt, refresh, apply } = useGroupBasket({
    basketId,
    userId,
    onChange,
  });

  // The host's own order has no status on the basket: the rail's answer says it.
  const onRailAnswer = useCallback((payment, outcome) => {
    if (payment.role !== PAY_PURPOSE.CHECKOUT) return;
    if (outcome === "successful") toast.success("Payment confirmed.");
    if (outcome === "failed") toast.error("The payment did not go through. Open the order to pay again.");
    forgetRef.current();
  }, []);

  const { payment, track, forget } = useGroupPaymentWatch({ basketId, refresh, onRailAnswer });
  paymentRef.current = payment;
  forgetRef.current = forget;

  // Back from Flutterwave (mobile money or a card's 3-D Secure): check that
  // payment at once, then drop the provider's query from the address.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!RETURN_PARAMS.some((key) => params.has(key))) return;
    const respObj = params.get("resp");
    if (respObj && userId) {
      dispatch(verifyAirtelPayment({ userId, respObj }))
        .catch(() => {})
        .finally(() => refresh());
    } else if (params.has("status") || params.has("tx_ref")) {
      refresh();
    }
    history.replace(location.pathname);
    // Read once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isHost = isHostOf(basket);
  const canEditLines = basket?.permissions?.canEditLines === true;
  const menu = useRestaurantMenuSections({
    vendorId: basket?.vendor?.vendorId,
    outletId: basket?.vendor?.outletId || null,
    enabled: canEditLines,
  });
  const productsById = useMemo(() => {
    const map = new Map();
    (menu.sections || []).forEach((section) =>
      (section.products || []).forEach((product) => map.set(String(product._id), product))
    );
    return map;
  }, [menu.sections]);

  /* Run one basket call; the basket it answers with replaces the one on screen. */
  const run = async (call, { onError } = {}) => {
    setBusy(true);
    try {
      const next = await call();
      if (next) apply(next);
      return next || true;
    } catch (err) {
      if (onError) onError(err);
      else toast.error(err.message);
      if (["INVALID_STATE", "HOST_ONLY", "BASKET_NOT_FOUND"].includes(err.code)) refresh();
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveLine = async (line) => {
    const saved = await run(() =>
      saveMyGroupLines(basketId, linesWithSaved(basket, line, dish?.line?.lineId || null))
    );
    if (saved) {
      if (!dish?.line) toast.success("Added to your items.");
      setDish(null);
    }
  };

  const removeLine = (line) => run(() => saveMyGroupLines(basketId, linesWithout(basket, line.lineId)));

  const editLine = (line) => {
    const product = productsById.get(String(line.productId));
    if (product) setDish({ product, line });
  };

  const onControl = (control) => {
    if (control === CONTROL.ADD_ITEMS) setMenuOpen(true);
    else if (control === CONTROL.LOCK) run(() => lockGroupBasket(basketId));
    else if (control === CONTROL.OPEN_ORDER) history.push(`/orderDetail/${basket.order.orderId}`);
    else if (PAY_PURPOSE_OF[control]) setPayPurpose(PAY_PURPOSE_OF[control]);
    else if (control === CONTROL.LEAVE) setConfirm({ kind: CONTROL.LEAVE });
    else if (control === CONTROL.CANCEL) {
      setCancelReason("");
      setCancelNeedsConfirmation(cancelNeedsRefundConfirmation(basket));
      setConfirm({ kind: CONTROL.CANCEL });
    }
  };

  const closeConfirm = () => {
    setConfirm(null);
    setConfirmError("");
  };

  const confirmAction = async () => {
    if (!confirm) return;
    setConfirmError("");
    const onError = (err) => {
      // The server asks for the refund confirmation when a share was paid.
      if (confirm.kind === CONTROL.CANCEL && err.code === REFUND_CONFIRMATION_REQUIRED) {
        setCancelNeedsConfirmation(true);
      }
      setConfirmError(err.message);
    };
    if (confirm.kind === CONTROL.CANCEL) {
      const done = await run(
        () =>
          cancelGroupBasket(basketId, {
            reason: cancelReason.trim() || undefined,
            confirmRefunds: cancelNeedsConfirmation,
          }),
        { onError }
      );
      if (done) closeConfirm();
      return;
    }
    if (confirm.kind === CONTROL.LEAVE) {
      const done = await run(() => removeGroupMember(basketId, basket.you.memberId), { onError });
      if (done) {
        forgetMyGroupBaskets();
        toast.success("You left the basket.");
        history.replace(restaurantPathOf(basket.vendor));
      }
      return;
    }
    const done = await run(() => removeGroupMember(basketId, confirm.member.memberId), { onError });
    if (done) closeConfirm();
  };

  const confirmCopy = (() => {
    if (!confirm) return null;
    const label = confirm.member?.label;
    switch (confirm.kind) {
      case CONTROL.CANCEL:
        return {
          title: "Cancel this basket?",
          message: "Nobody can add items or pay after this. The order is not placed.",
          confirmLabel: cancelNeedsConfirmation ? "Cancel and refund" : "Cancel basket",
        };
      case CONTROL.LEAVE:
        return {
          title: "Leave this basket?",
          message: "Your items are taken out. You can join again from the link while it is open.",
          confirmLabel: "Leave basket",
        };
      case MEMBER_CONTROL.REMOVE:
        return {
          title: `Remove ${label}?`,
          message: `${label}'s items go with them.`,
          confirmLabel: `Remove ${label}`,
        };
      case MEMBER_CONTROL.DROP:
        return {
          title: `Drop ${label}'s items?`,
          message: `${label} did not pay. Their items are taken out of the order and what is left to pay is worked out again. Paid shares do not change.`,
          confirmLabel: "Drop items",
        };
      default:
        return null;
    }
  })();

  if (status === "loading") {
    return (
      <Page>
        <Navbar bgColor="#2f474e" />
        <Hero>
          <HeroInner>
            <Eyebrow $onDark>Order together</Eyebrow>
            <HeroTitle>Loading the basket</HeroTitle>
          </HeroInner>
        </Hero>
        <Shell>
          <Card aria-busy="true">
            <Muted>One moment.</Muted>
          </Card>
        </Shell>
      </Page>
    );
  }

  if (!basket) {
    const gone = status === "gone";
    return (
      <Page>
        <Navbar bgColor="#2f474e" />
        <Hero>
          <HeroInner>
            <Eyebrow $onDark>Order together</Eyebrow>
            <HeroTitle>{gone ? "You are not in this basket" : "The basket could not be loaded"}</HeroTitle>
          </HeroInner>
        </Hero>
        <Shell>
          <Card>
            <Muted>
              {gone
                ? "It may have ended, or the host removed you. Ask the host for the link to join again."
                : error?.message || "Check your connection and try again."}
            </Muted>
            <Actions>
              {!gone && (
                <PrimaryButton type="button" onClick={refresh}>
                  Try again
                </PrimaryButton>
              )}
              <QuietButton as={Link} to="/restaurants">
                Browse restaurants
              </QuietButton>
            </Actions>
          </Card>
        </Shell>
      </Page>
    );
  }

  const lead = leadOf(basket);
  const controls = basketControls(basket);
  const dropoff = basket.dropoff || {};
  const droppable = (basket.members || []).filter((member) =>
    memberControls(basket, member).includes(MEMBER_CONTROL.DROP)
  );

  return (
    <Page>
      <Navbar bgColor="#2f474e" />
      <Hero>
        <HeroInner $wide>
          <Eyebrow $onDark>Order together</Eyebrow>
          <HeroTitle>{basket.vendor?.name}</HeroTitle>
          <HeroLine>
            <GroupsOutlinedIcon aria-hidden="true" />
            <span>
              {isHost ? "You are the host" : `Hosted by ${basket.hostLabel}`}. {modeSummary(basket)}.
            </span>
          </HeroLine>
          {dropoff.address && (
            <HeroLine>
              <PlaceOutlinedIcon aria-hidden="true" />
              <span>
                {dropoff.address}
                {dropoff.description ? `, ${dropoff.description}` : ""}
              </span>
            </HeroLine>
          )}
          <PillRow>
            <Pill $tone="dark">{stateLabel(basket.state)}</Pill>
            <GroupBasketCountdown
              deadline={basket.deadlines?.active}
              serverNow={basket.deadlines?.serverNow}
              receivedAt={receivedAt}
            />
          </PillRow>
        </HeroInner>
      </Hero>

      <Shell $wide>
        {justCreated && basket.shareUrl && (
          <GroupBasketShare shareUrl={basket.shareUrl} shareText={basket.shareText} justCreated />
        )}

        <Card aria-live="polite">
          <CardTitle>{lead.title}</CardTitle>
          {lead.detail ? <Muted>{lead.detail}</Muted> : null}
          {basket.state === "CANCELLED" && (
            <Actions>
              <PrimaryButton as={Link} to={restaurantPathOf(basket.vendor)}>
                <span aria-hidden="true">← </span>
                {basket.vendor?.vendorId ? "Back to restaurant" : "Browse restaurants"}
              </PrimaryButton>
            </Actions>
          )}
          {(controls.primary.length > 0 || controls.more.length > 0) && (
            <Actions>
              {controls.primary.map((control, index) => {
                const Button = index === 0 ? PrimaryButton : InkButton;
                return (
                  <Button key={control} type="button" disabled={busy} onClick={() => onControl(control)}>
                    {CONTROL_LABELS[control]}
                  </Button>
                );
              })}
              {controls.more.map((control) => (
                <QuietButton key={control} type="button" disabled={busy} onClick={() => onControl(control)}>
                  {CONTROL_LABELS[control]}
                </QuietButton>
              ))}
            </Actions>
          )}
          {droppable.length > 0 && (
            <DropList aria-label="Members who did not pay">
              {droppable.map((member) => (
                <li key={member.memberId}>
                  <span>
                    <strong>{member.label}</strong> did not pay
                  </span>
                  <TextButton
                    type="button"
                    $danger
                    disabled={busy}
                    onClick={() => setConfirm({ kind: MEMBER_CONTROL.DROP, member })}
                  >
                    Drop {member.label}&rsquo;s items
                  </TextButton>
                </li>
              ))}
            </DropList>
          )}
        </Card>

        {!justCreated && basket.shareUrl && (
          <GroupBasketShare shareUrl={basket.shareUrl} shareText={basket.shareText} />
        )}

        {basket.order && <GroupBasketOrder order={basket.order} />}

        <BasketColumns>
        <GroupBasketMoney basket={basket} />

        <GroupBasketMembers
          basket={basket}
          canEditLines={canEditLines}
          menuReady={menu.status === "ready"}
          busy={busy}
          onAddItems={() => setMenuOpen(true)}
          onEditLine={editLine}
          onRemoveLine={removeLine}
          onMemberControl={(member, kind) => setConfirm({ kind, member })}
        />

        </BasketColumns>

        {controls.quiet.length > 0 && (
          <Quiet>
            {controls.quiet.map((control) => (
              <TextButton key={control} type="button" $danger disabled={busy} onClick={() => onControl(control)}>
                {control === CONTROL.LEAVE ? "Leave this basket" : "Cancel this basket"}
              </TextButton>
            ))}
          </Quiet>
        )}
      </Shell>

      {menuOpen && (
        <GroupMenuSheet
          open={menuOpen}
          menu={menu}
          myLines={myMemberOf(basket)?.lines || []}
          restaurantName={basket.vendor?.name}
          currency={basket.currency}
          onPickDish={(product) => setDish({ product, line: null })}
          onClose={() => setMenuOpen(false)}
        />
      )}

      {dish && (
        <GroupDishDialog
          open={Boolean(dish)}
          product={dish.product}
          line={dish.line}
          currency={basket.currency}
          busy={busy}
          onSave={saveLine}
          onClose={() => setDish(null)}
        />
      )}

      {payPurpose && (
        <GroupPaySheet
          open={Boolean(payPurpose)}
          purpose={payPurpose}
          basket={basket}
          receivedAt={receivedAt}
          user={user}
          onBasket={apply}
          onStarted={({ rail }) => {
            track();
            refresh();
            if (rail === "COD") toast.success("Order placed. You pay the rider on delivery.");
            else if (rail === "MTN_DIRECT") toast.info("Approve the payment prompt on your phone.");
          }}
          onClose={() => setPayPurpose(null)}
        />
      )}

      {confirm && confirmCopy && (
        <GroupConfirmDialog
          open
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmLabel={confirmCopy.confirmLabel}
          destructive
          busy={busy}
          error={confirmError}
          onConfirm={confirmAction}
          onClose={closeConfirm}
        >
          {confirm.kind === CONTROL.CANCEL ? (
            <>
              {cancelNeedsConfirmation && (
                <Notice $tone="wait" role="note">
                  <strong>{REFUND_CONFIRMATION_TEXT}.</strong>
                </Notice>
              )}
              <div>
                <FieldLabel htmlFor="group-cancel-reason">Reason (optional)</FieldLabel>
                <TextInput
                  id="group-cancel-reason"
                  value={cancelReason}
                  maxLength={140}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Plans changed"
                />
              </div>
            </>
          ) : null}
        </GroupConfirmDialog>
      )}
    </Page>
  );
};

const DropList = styled.ul`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  border-top: 1px solid ${LINE};

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    font-size: 15px;
    color: ${MUTED};
  }

  li + li {
    border-top: 1px solid ${LINE};
  }

  strong {
    color: ${TEXT};
    font-weight: 800;
  }
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const Quiet = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export default GroupBasketPage;

const BasketColumns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  }
`;
